import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Palette, Search, Filter, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export function TemplatesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
        const [templatesRes, categoriesRes] = await Promise.all([
          api.get(`/api/templates${params}`),
          api.get('/api/template-categories'),
        ]);
        setTemplates(templatesRes.data);
        setCategories(categoriesRes.data);
      } catch {
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, selectedCategory]);

  const filtered = templates.filter(t =>
    !searchQuery || t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm text-coral uppercase tracking-[0.2em] font-semibold">
            Design Library
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-navy">
            Design Templates
          </h1>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            Browse our collection of professionally designed templates. Use them as a starting point for your project or order directly.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="template-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48" data-testid="template-category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Palette className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No templates found</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden card-hover group" data-testid={`template-card-${template.id}`}>
                  {/* Thumbnail */}
                  <div className="product-image-container aspect-[4/3] relative">
                    <img
                      src={template.thumbnail_url}
                      alt={template.name}
                      className="w-full h-full object-cover product-image"
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-navy/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button className="bg-mint hover:bg-mint-dark text-black rounded-full">
                        Preview Template
                      </Button>
                    </div>
                    {/* Price badge */}
                    <div className="absolute top-3 right-3">
                      {template.is_free ? (
                        <Badge className="bg-mint text-black font-semibold">Free</Badge>
                      ) : (
                        <Badge className="bg-navy text-white font-semibold">${template.price?.toFixed(2)}</Badge>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5">
                    <span className="text-xs text-coral uppercase tracking-wider font-medium">
                      {template.category}
                    </span>
                    <h3 className="font-heading font-semibold text-navy mt-1 text-lg">
                      {template.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                      {template.description}
                    </p>
                    <div className="mt-4 flex gap-3">
                      <Link to={`/templates/${template.id}/edit`} className="flex-1">
                        <Button className="w-full bg-mint hover:bg-mint-dark text-black rounded-full text-sm" data-testid={`use-template-${template.id}`}>
                          Use Template
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
